import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useLocale();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`¡Hola, ${u.name || u.email}!`);
      const to = location.state?.from?.pathname || (u.role === "agente" ? "/dashboard" : "/");
      navigate(to, { replace: true });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto max-w-md px-6 md:px-0 py-16 md:py-24">
      <div className="mb-8">
        <div className="label-eyebrow text-secondary">To2casa</div>
        <h1 className="mt-2 font-serif text-5xl leading-none">{t("auth.login_title")}</h1>
        <p className="mt-2 text-muted-foreground">{t("auth.login_sub")}</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="label-eyebrow block mb-2">{t("auth.email")}</label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">{t("auth.password")}</label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full btn-hover-lift" data-testid="login-submit">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {t("auth.submit_login")}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-4">
        <div className="h-px flex-1 bg-border" />
        <span className="label-eyebrow text-muted-foreground">{t("auth.or")}</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <Button variant="outline" onClick={onGoogle} className="w-full rounded-full" data-testid="google-login">
        <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
        {t("auth.google")}
      </Button>

      <p className="mt-8 text-sm text-muted-foreground text-center">
        {t("auth.no_account")}{" "}
        <Link to="/register" className="text-primary hover:underline underline-offset-4" data-testid="go-register">
          {t("auth.go_register")}
        </Link>
      </p>
    </div>
  );
}
